import { IClientsPartners, IPartners } from '@llws/lift-entity-interfaces';
import { Equal, IQueryCommandOptions, Not } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IMetaResponse,
  IModifyPayload,
  IRemovePayload,
} from '@/shared';
import { IAction } from '@/shared/interfaces/store-base';
import partners from '@/shared/services/partners';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IPartnersStore } from '@/shared/interfaces/partners-store';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/partners';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  where: [ Not(Equal('status', 'disabled')), Equal('syndication', 'live') ],
  orderBy: [{ field: 'syndicationOrder', direction: 'asc' }, { field: 'name', direction: 'asc' }],
  limit: 999,
  relations: [
    {
      path: 'promotedListingTypes',
      alias: 'plt',
    },
    {
      path: 'plt.contractBaseSubscription',
      alias: 'cbs',
    },
    {
      path: 'cbs.variableBasePrices',
      alias: 'vbp',
    },
  ],
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  relations: [
    {
      path: 'partnerRentsyncSettings',
      alias: 'prs',
    },
    {
      path: 'partnerRentsyncRestrictions',
      alias: 'prr',
    },
    {
      path: 'promotedListingTypes',
      alias: 'plt',
    },
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IPartners>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'partners/'

  state: {
    ...base.state,

    clientPartners: null,
  },

  getters: {
    ...base.getters,

    clientPartners: (state: IPartnersStore) => [
      ...state.clientPartners?.data
        ? state.clientPartners.data?.map((cp: IClientsPartners) => ({ ...cp.partner! }))
        : [],
    ],

    clientPartnerKeys: (state: IPartnersStore) => [
      ...state.clientPartners?.data
        ? state.clientPartners.data?.map((cp: IClientsPartners) => (cp.partner!.partnerKey))
        : [],
    ],

    clientPartnerJunctions: (state: IPartnersStore) => state.clientPartners?.data || [],
  },

  mutations: {
    ...base.mutations,

    setClientPartners: (
      state: IPartnersStore,
      payload: IApiResponse<IClientsPartners[], IMetaResponse>,
    ) => {
      state.clientPartners = Array.isArray(payload)
        ? { ...state.clientPartners && state.clientPartners, ...{ data: payload } }
        : payload;
    },
  },

  actions: {
    ...base.actions,

    getClientPartners: async (ctx: IAction<IPartnersStore>) => {
      const result = await partners.getClientPartners();
      ctx.commit('setClientPartners', result);
    },

    addClientPartner: (ctx: IAction<IPartnersStore>, payload: IModifyPayload) => adminRequest.post('/syndication-api/clients_partners', payload.data),

    removeClientPartner: (ctx: IAction<IPartnersStore>, payload: IRemovePayload) => adminRequest.delete('/syndication-api/clients_partners', [payload.id]),
  },
};
